import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Share2, Calendar, FileText, RefreshCw } from "lucide-react";
import PublicPageNav from "./components/PublicPageNav";
import PublicPageFooter from "./components/PublicPageFooter";
import { BACKEND_URL } from "./lib/backend";

const API = BACKEND_URL + "/api";
const SITE = "https://tourismintel.ai";

function setMeta(name, content, isOg = false) {
  const sel = isOg ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let el = document.querySelector(sel);
  if (!el) { el = document.createElement("meta"); el.setAttribute(isOg ? "property" : "name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", url);
}

export default function PublicNewsletterPage() {
  const { newsletterId } = useParams();
  const navigate = useNavigate();
  const [newsletter, setNewsletter] = useState(null);
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setNotFound(false);
    try {
      const [metaRes, htmlRes] = await Promise.allSettled([
        axios.get(`${API}/newsletters/public/${newsletterId}`),
        axios.get(`${API}/newsletters/html/${newsletterId}?body_only=true`, { responseType: "text" }),
      ]);
      if (metaRes.status === "fulfilled") {
        const nl = metaRes.value.data.newsletter;
        setNewsletter(nl);
        // Legacy UUID URL → redirect to the human-readable slug URL
        if (nl?.seo_slug && newsletterId !== nl.seo_slug) {
          navigate(`/newsletter/${nl.seo_slug}`, { replace: true });
        }
      } else setNotFound(true);
      if (htmlRes.status === "fulfilled") setHtmlContent(htmlRes.value.data);
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  }, [newsletterId, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!newsletter) return;
    const canonicalSlug = newsletter.seo_slug || newsletterId;
    const pageUrl = `${SITE}/newsletter/${canonicalSlug}`;
    const title = `${newsletter.title} | TourismIntel`;
    const desc = (newsletter.content || "").slice(0, 155).replace(/\s+/g, " ").trim() + "…";
    document.title = title;
    setMeta("description", desc); setMeta("og:title", title, true);
    setMeta("og:description", desc, true); setMeta("og:url", pageUrl, true);
    setMeta("og:type", "article", true); setMeta("twitter:card", "summary_large_image");
    setCanonical(pageUrl);
    // Structured data (rich results / CTR): Article JSON-LD per issue
    document.getElementById("newsletter-jsonld")?.remove();
    const ld = document.createElement("script");
    ld.id = "newsletter-jsonld";
    ld.type = "application/ld+json";
    const pubDate = newsletter.published_at || newsletter.sent_at || newsletter.created_at;
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: newsletter.title,
      description: desc,
      ...(pubDate ? { datePublished: new Date(pubDate).toISOString() } : {}),
      author: { "@type": "Person", name: "Mirko Lalli", url: "https://mirkolalli.me" },
      publisher: {
        "@type": "NewsMediaOrganization",
        name: "TourismIntel",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/logo512.png`, width: 512, height: 512 },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
      image: `${SITE}/og-image.jpg?v=20261029`,
      isPartOf: { "@type": "CreativeWorkSeries", name: "TourismIntel Newsletter", url: `${SITE}/newsletters` },
    });
    document.head.appendChild(ld);
    return () => { document.getElementById("newsletter-jsonld")?.remove(); };
  }, [newsletter, newsletterId]);

  // noindex on confirmed 404 — prevents soft-404 indexing of dead newsletter URLs
  useEffect(() => {
    if (!notFound) return;
    const robots = document.querySelector('meta[name="robots"]');
    const prev = robots?.getAttribute("content");
    if (robots) robots.setAttribute("content", "noindex, follow");
    return () => { if (robots && prev) robots.setAttribute("content", prev); };
  }, [notFound]);

  const handleShare = () => {
    navigator.clipboard.writeText(`${window.location.origin}/newsletter/${newsletter?.seo_slug || newsletterId}`)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : "";

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#f8f9fa" }}>
      <PublicPageNav active="newsletter" />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <RefreshCw style={{ width: 28, height: 28, color: "#006874", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: "100vh", background: "#f8f9fa" }}>
      <PublicPageNav active="newsletter" />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 16, padding: "0 24px" }}>
        <FileText style={{ width: 40, height: 40, color: "#bac9cc" }} />
        <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#191c1d", margin: 0 }}>Newsletter not found</h1>
        <p style={{ color: "#6b7a7d", margin: 0 }}>Not available or not yet published.</p>
        <Link to="/newsletters" style={{ marginTop: 8, padding: "10px 24px", borderRadius: 99, background: "#006874", color: "#fff", textDecoration: "none", fontWeight: 600, fontSize: 14 }}>
          Browse all newsletters →
        </Link>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fa", fontFamily: "'Space Grotesk', sans-serif" }}>
      <PublicPageNav active="newsletter" />

      <div style={{ maxWidth: 700, margin: "0 auto", padding: "48px 24px 0" }}>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#006874", marginBottom: 14 }}>
          Travel Intelligence — Newsletter
        </p>
        <h1 style={{ fontFamily: "'Newsreader', serif", fontSize: "clamp(1.7rem, 4vw, 2.5rem)", fontWeight: 500, lineHeight: 1.15, color: "#191c1d", marginBottom: 20, letterSpacing: "-0.02em" }}>
          {newsletter.title}
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#6b7a7d" }}>
            <Calendar style={{ width: 13, height: 13 }} />
            {fmt(newsletter.published_at || newsletter.created_at)}
          </div>
          {newsletter.word_count > 0 && (
            <span style={{ fontSize: 12, color: "#bac9cc" }}>{newsletter.word_count} words · {newsletter.article_count} articles</span>
          )}
          <button
            onClick={handleShare}
            data-testid="newsletter-share-btn"
            style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 99, border: "1.5px solid #e8ecee", background: copied ? "#006874" : "#fff", color: copied ? "#fff" : "#191c1d", fontSize: 12, fontWeight: 500, cursor: "pointer", transition: "all 0.2s" }}
          >
            <Share2 style={{ width: 12, height: 12 }} />
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
        <hr style={{ border: "none", borderTop: "1px solid #e8ecee", marginBottom: 0 }} />
      </div>

      {/* Newsletter content: native DOM rendering (real h2/h3 for SEO/GEO) */}
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "24px 24px 40px" }}>
        {htmlContent ? (
          <article
            data-testid="newsletter-content"
            style={{ background: "#fff", borderRadius: 16, padding: "36px clamp(22px, 5vw, 42px)", boxShadow: "0 0 0 1px rgba(186,201,204,0.18), 0 4px 24px rgba(25,28,29,0.04)" }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : (
          <div style={{ padding: "48px 24px", textAlign: "center", color: "#bac9cc" }}>Content not available.</div>
        )}
      </div>

      <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 24px 80px" }}>
        <div style={{ borderRadius: 16, background: "#006874", textAlign: "center", padding: "32px" }}>
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginBottom: 10 }}>Stay ahead of the curve</p>
          <p style={{ fontFamily: "'Newsreader', serif", fontSize: "1.4rem", fontWeight: 500, color: "#fff", marginBottom: 20, lineHeight: 1.3 }}>
            All the stories that matter in AI and travel, explained. A weekly brief, newsletter and podcast, plus Pulse: Italy&apos;s tourism in official numbers and forecasts.
          </p>
          <Link to="/" style={{ display: "inline-block", padding: "12px 28px", borderRadius: 99, background: "#fff", color: "#006874", textDecoration: "none", fontWeight: 700, fontSize: 14 }}>
            Join TourismIntel — free
          </Link>
        </div>
      </div>

      <PublicPageFooter />
    </div>
  );
}
